import { observer } from 'mobx-react-lite'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid'
import { FeedbackPresenter } from './feedback.presenter'
import type { FeedbackScope, MessageTone } from './feedback.config'
import { useInjection } from '../providers/inversify-react.provider'

// Literal colors, not shell-* tokens — same reasoning as NotificationsComponent:
// these carry meaning (success/error/warning), not decoration.
// Literal colors, keyed by tone. success/error arrive from a derived action
// result; warning/info only ever from FeedbackPresenter.show().
const TONE: Record<MessageTone, { Icon: typeof CheckCircleIcon; className: string }> = {
  success: { Icon: CheckCircleIcon, className: 'text-green-500' },
  error: { Icon: XCircleIcon, className: 'text-red-500' },
  warning: { Icon: ExclamationTriangleIcon, className: 'text-amber-500' },
  info: { Icon: InformationCircleIcon, className: 'text-shell-accent' },
}

const CARD = 'flex items-start gap-x-2 rounded-md border border-shell-border bg-shell-surface p-3 shadow-lg'

type FeedbackCardProps = {
  message: string
  tone: MessageTone
  /** Omit for a message with nothing to dismiss it *to* — a computed, read-only
   * derivation (e.g. a Presenter's own `loadState`-derived error) has no
   * underlying write to clear, so there is nothing a dismiss button could do. */
  onDismiss?: () => void
  dismissLabel?: string
}

/** Presentational only — no scope, no Presenter. Used both by FeedbackComponent
 * (write-based channels) and directly by a Presenter's own computed VM fields
 * (e.g. a route-entry load failure), so both render identically. */
export const FeedbackCard = ({ message, tone, onDismiss, dismissLabel }: FeedbackCardProps) => {
  const { Icon, className } = TONE[tone]

  return (
    <div role="alert" className={CARD}>
      <Icon className={`size-5 shrink-0 ${className}`} aria-hidden="true" />
      <p className="flex-1 text-sm text-shell-ink">{message}</p>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="shrink-0 rounded-md p-0.5 text-shell-ink-muted hover:text-shell-ink"
        >
          <XMarkIcon className="size-4" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

type Props = {
  /** Which channel to render. One mounted component per feedback surface. */
  scope: FeedbackScope
}

export const FeedbackComponent = observer(({ scope }: Props) => {
  const presenter: FeedbackPresenter = useInjection(FeedbackPresenter)

  const vm = presenter.feedbackVmFor(scope)

  if (!vm.showFeedback) return null

  // No own fixed positioning — whichever surface mounts this decides placement
  // (TopRightStackComponent for the app scope, the page itself for inline ones).
  return (
    <>
      {vm.messages.map((message, i) => (
        <FeedbackCard
          key={i}
          message={message}
          tone={vm.tone}
          onDismiss={() => presenter.dismiss(scope)}
          dismissLabel={vm.dismissLabel}
        />
      ))}
    </>
  )
})
